import React, {
  createContext,
  useContext,
  useEffect, useRef,
  useState,
} from "react";
import useDebounce from "hooks/useDebounce";
import { useWallDataContext } from "./WallContext";
import { IDraftChapter, IDraftParagraph } from "models/wall";
import {
  createEmptyDraftChapter,
  deleteDraftChapter,
  updateDraftChapterApi
} from "api/draftChapters";
import { deleteDraftParagraph, moveDraftParagraphDown, moveDraftParagraphUp } from "api/paragraphs";

const emptyDraft = {
  title: "",
  date_from_day: null,
  date_from_month: null,
  date_from_year: null,
  date_to_day: null,
  date_to_month: null,
  date_to_year: null,
  draft_paragraphs: [],
  is_hidden: false,
}

type ChapterDataContextType = {
  draftChapter: IDraftChapter;
  updateDraftChapter: (v: Partial<IDraftChapter>) => void;
  deleteParagraph: (uuid: string) => void;
  isSaving: boolean;
  setIsSaving: (v: boolean) => void;
  moveParagraphUp: (uuid: string) => void;
  moveParagraphDown: (uuid: string) => void;
  deleteChapter: (uuid: string) => void;
  updateParagraphInChapter: (uuid: string, patch: Partial<IDraftParagraph>) => void;
};

const ChapterEditorDataContext = createContext<ChapterDataContextType | undefined>(
  undefined
);

export const ChapterEditorDataProvider = ({children, storyId, initialDraft}: {
  children: React.ReactNode;
  storyId: string;
  initialDraft?: IDraftChapter;
}) => {
  const { addDraftChapter } = useWallDataContext();
  const [draftChapter, setDraftChapter] = useState<IDraftChapter>(initialDraft ?? emptyDraft);

  const [isSaving, setIsSaving] = useState(false);

  const updateDraftChapter = (v: Partial<IDraftChapter>) => {
    setDraftChapter((p) => ({...p, ...v}));
  };

  const debouncedDraft = useDebounce(draftChapter, 400);

  const prevDraftRef = useRef<IDraftChapter>(debouncedDraft);

  const updateChapter = async () => {
    if (!draftChapter.uuid || !draftChapter.title) return;
    const patch = Object.fromEntries(
      Object.entries(debouncedDraft).filter(([key, value]) => {
        const typedKey = key as keyof IDraftChapter;
        if (typedKey === "draft_paragraphs") return false;
        return value !== prevDraftRef.current[typedKey];
      })
    ) as Partial<IDraftChapter>;
    if (Object.keys(patch).length === 0) return;

    setIsSaving(true);

    try {
      await updateDraftChapterApi(draftChapter.uuid, patch);

      prevDraftRef.current = debouncedDraft;
    } catch (e) {
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    updateChapter();
  }, [debouncedDraft]);

  const createDraftChapter = async () => {
    if (debouncedDraft.uuid || !debouncedDraft.title) return;
    setIsSaving(true);

    try {
      const response = await createEmptyDraftChapter({
        ...debouncedDraft,
        story: storyId,
      });
      setDraftChapter(response);
      addDraftChapter(response);
    } catch (e) {
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    createDraftChapter();
  }, [debouncedDraft]);

  const deleteChapter = async () => {
    if (!draftChapter.original_chapter || !draftChapter.uuid || !initialDraft) return;
    setIsSaving(true);
    try {
      await deleteDraftChapter(draftChapter.uuid);
    } catch (e) {
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  };

  const updateParagraphInChapter = (uuid: string, patch: Partial<IDraftParagraph>) => {
    const orig = draftChapter.draft_paragraphs.find(p => p.uuid === uuid);
    if (!orig) return;

    const updated: IDraftParagraph = { ...orig, ...patch };
    const newArr = [
      ...draftChapter.draft_paragraphs.filter(p => p.uuid !== uuid),
      updated
    ].sort((a, b) => a.order - b.order);


    const updatedChapter = { ...draftChapter, draft_paragraphs: newArr };

    setDraftChapter(updatedChapter);
    if (!updatedChapter.original_chapter) addDraftChapter(updatedChapter);
  }

  const deleteParagraph = async (uuid: string) => {
    setIsSaving(true);

    try {
      await deleteDraftParagraph(uuid);
      setDraftChapter((prev) => ({
        ...prev,
        draft_paragraphs: prev.draft_paragraphs.filter(
          (p) => p.uuid !== uuid
        ),
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  };

  const moveParagraphUp = async (uuid: string) => {
    const index = draftChapter.draft_paragraphs.findIndex((p) => p.uuid === uuid);
    if (index <= 0) return;
    setIsSaving(true);
    try {
      await moveDraftParagraphUp(uuid);
      setDraftChapter((prev) => {
        const copy = [...prev.draft_paragraphs];
        const idx = copy.findIndex((p) => p.uuid === uuid);
        if (idx <= 0) return prev;
        // Swap order property between this paragraph and the one above it
        const temp = copy[idx].order;
        copy[idx].order = copy[idx - 1].order;
        copy[idx - 1].order = temp;
        // Return a new draft with the paragraphs sorted by order
        return {
          ...prev,
          // @ts-ignore
          draft_paragraphs: copy.sort((a, b) => a.order - b.order),
        };
      });
    } catch (e) {
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  };

  const moveParagraphDown = async (uuid: string) => {
    const index = draftChapter.draft_paragraphs.findIndex((p) => p.uuid === uuid);
    if (index < 0 || index === draftChapter.draft_paragraphs.length - 1) return;
    setIsSaving(true);
    try {
      await moveDraftParagraphDown(uuid);
      setDraftChapter((prev) => {
        const copy = [...prev.draft_paragraphs];
        const idx = copy.findIndex((p) => p.uuid === uuid);
        if (idx < 0 || idx === copy.length - 1) return prev;
        // Swap order property between this paragraph and the one below it
        const temp = copy[idx].order;
        copy[idx].order = copy[idx + 1].order;
        copy[idx + 1].order = temp;
        return {
          ...prev,
          // @ts-ignore
          draft_paragraphs: copy.sort((a, b) => a.order - b.order),
        };
      });
    } catch (e) {
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ChapterEditorDataContext.Provider
      value={{
        draftChapter,
        updateDraftChapter,
        deleteParagraph,
        isSaving,
        setIsSaving,
        moveParagraphUp,
        moveParagraphDown,
        deleteChapter,
        updateParagraphInChapter
      }}
    >
      {children}
    </ChapterEditorDataContext.Provider>
  );
};

export const useChapterEditorDataContext = () => {
  const context = useContext(ChapterEditorDataContext);
  if (!context) {
    throw new Error("useChapterEditorDataContext must be used within a ChapterEditorDataProvider");
  }
  return context;
};
